"use client";

import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import useAuthStore from "@/lib/store/authStore";
import { motion } from "framer-motion";

// Componentes reutilizables
import ImageUploader from "@/app/components/SignUp/ImageUploader";
import FormField from "@/app/components/SignUp/FormField";
import SelectField from "@/app/components/SignUp/SelectField";
import ActionButtons from "@/app/components/SignUp/ActionButtons";
import MultiSelectField from "@/app/components/SignUp/MultiSelectField";
import InstitutionToggle from "@/app/components/SignUp/InstitutionToggle";
import EmailDisplay from "@/app/components/SignUp/EmailDisplay";

interface Props {
  email: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  password: string;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  monedas: any[];
  paises: any[];
  localidades: any[];
  deportes: any[];
  roleData: {
    rolId: number;
    codigo: number;
    authority: string;
    descripcion: string;
    permisos: string;
  };
  onPaisChange: (paisId: number) => void;
}

interface ImagenPerfil {
  tipo: string;
  nombre: string;
  archivo: string;
}

const InstructorSignUp = ({
  email,
  password,
  setOpen,
  step,
  setStep,
  roleData,
  paises,
  monedas,
  localidades,
  deportes,
  onPaisChange,
}: Props) => {
  // Estados para los datos del backend
  const [imagenPerfil, setImagenPerfil] = useState<ImagenPerfil>({
    tipo: "",
    nombre: "",
    archivo: "",
  });
  const [username, setUsername] = useState("");
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [localidad, setLocalidad] = useState({
    localidadId: 0,
    nombreLocalidad: "",
    paisId: 0,
    pais: "",
  });
  const [sexo, setSexo] = useState("");
  const [direccion, setDireccion] = useState("");
  const [institucion, setInstitucion] = useState(false);
  const [nombreInstitucion, setNombreInstitucion] = useState("");
  const [numeroInstitucion, setNumeroInstitucion] = useState("");
  const [pais, setPais] = useState("");
  const [deportesSel, setDeportes] = useState<string[]>([]);
  const [moneda, setMoneda] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localidadesFiltradas, setLocalidadesFiltradas] = useState<any[]>([]);

  // Estados para la validación
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isNombreValid, setIsNombreValid] = useState(true);
  const [isTelefonoValid, setIsTelefonoValid] = useState(true);
  const [isDescripcionValid, setIsDescripcionValid] = useState(true);
  const [isDireccionValid, setIsDireccionValid] = useState(true);
  const [isSexoValid, setIsSexoValid] = useState(true);
  const [isFechaNacimientoValid, setIsFechaNacimientoValid] = useState(true);
  const [isImagenValid, setIsImagenValid] = useState(true);

  useEffect(() => {
    if (pais) {
      const selectedPais = paises.find((p) => p.nombrePais === pais);
      if (selectedPais) {
        const filteredLocalidades = localidades.filter(
          (l) => l.paisId === selectedPais.paisId
        );
        setLocalidadesFiltradas(filteredLocalidades);
        setLocalidad({
          localidadId: 0,
          nombreLocalidad: "",
          paisId: 0,
          pais: "",
        });
      }
    }
  }, [pais, paises, localidades]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
  };

  const handleNombreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNombre(value);
    if (formSubmitted) {
      setIsNombreValid(value.trim().length > 0);
    }
  };

  const handleTelefonoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTelefono(value);
    if (formSubmitted) {
      setIsTelefonoValid(/^\d{10,15}$/.test(value));
    }
  };

  const handleDireccionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDireccion(value);
    if (formSubmitted) {
      setIsDireccionValid(value.trim().length > 0);
    }
  };

  const handleDescripcionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setDescripcion(value);
    if (formSubmitted) {
      setIsDescripcionValid(value.trim().length > 0);
    }
  };

  const handleFechaNacimientoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFechaNacimiento(value);
    if (formSubmitted) {
      setIsFechaNacimientoValid(value.trim().length > 0);
    }
  };

  const handleSexoChange = (value: string) => {
    setSexo(value);
    if (formSubmitted) {
      setIsSexoValid(value.trim().length > 0);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenPerfil({
          tipo: file.type,
          nombre: file.name,
          archivo: reader.result as string,
        });
        setIsImagenValid(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setFormSubmitted(true);
    setIsNombreValid(nombre.trim().length > 0);
    setIsTelefonoValid(/^\d{10,15}$/.test(telefono));
    setIsDireccionValid(direccion.trim().length > 0);
    setIsDescripcionValid(descripcion.trim().length > 0);
    setIsSexoValid(sexo.trim().length > 0);
    setIsFechaNacimientoValid(fechaNacimiento.trim().length > 0);
    setIsImagenValid(imagenPerfil.archivo !== "");
  };

  const handleSubmit = async () => {
    validateForm();

    // Validar todos los campos requeridos antes de enviar
    if (!isFormValid) {
      toast.error("Por favor complete todos los campos requeridos");
      return;
    }

    setIsLoading(true);

    const API_URL = process.env.NEXT_PUBLIC_API_URL as string;

    const selectedMoneda = monedas.find((m) => m.nombreMoneda === moneda);
    const selectedLocalidad = localidades.find(
      (l) => l.nombreLocalidad === localidad.nombreLocalidad
    );
    const selectedDeportes = deportesSel.map((deporteName) => {
      const deporte = deportes.find((d) => d.nombre === deporteName);
      return {
        deporteId: deporte?.deporteId || 0,
        nombre: deporte?.nombre || "",
        src: deporte?.src || "",
        orden: deporte?.orden || 0,
        img: deporte?.img || "",
      };
    });

    const dataToSend = {
      usuarioId: 0,
      username: email,
      userName: email,
      nombre,
      password,
      facebookId: 0,
      fotoPerfil: imagenPerfil.archivo,
      telefono,
      fechaNacimiento: fechaNacimiento || new Date().toISOString(),
      authority: roleData.authority || "ROLE_INSTRUCTOR",
      nombreRol: roleData.descripcion || "Instructor",
      imagenPerfil: {
        tipo: imagenPerfil.tipo || "png",
        nombre: imagenPerfil.nombre || "perfil",
        archivo: imagenPerfil.archivo,
      },
      localidad: {
        localidadId: selectedLocalidad?.localidadId || 0,
        nombreLocalidad: selectedLocalidad?.nombreLocalidad || "",
        paisId: selectedLocalidad?.paisId || 0,
        pais: selectedLocalidad?.pais || "",
        imgLocalidad: {
          tipo: "",
          nombre: "",
          archivo: "",
          esImgPrincipal: "false",
          size: 0,
        },
        img64: "",
      },
      profilePic: imagenPerfil.archivo,
      deportes: selectedDeportes,
      calificacion: 0,
      cantUsuariosQueLoCalificaron: 0,
      sexo: sexo || "",
      descripcion,
      numeroInstitucion: institucion ? numeroInstitucion : "",
      nombreInstitucion: institucion ? nombreInstitucion : "",
      tieneInstitucion: institucion,
      habilitado: false,
      precios: [],
      monedaClase: selectedMoneda
        ? {
          monedaId: selectedMoneda.monedaId,
          nombreMoneda: selectedMoneda.nombreMoneda,
        }
        : {
          monedaId: 0,
          nombreMoneda: "ARS",
        },
      comprobanteDePagoMandado: true,
      paseGratis: true,
    };

    try {
      const response = await axios.post(
        `${API_URL}/registerInstructor`,
        dataToSend
      );

      if (response.status === 200 && response.data && response.data.jwttoken) {
        // Guardar token y usuarioId en cookies
        Cookies.set("t_u", response.data.jwttoken, {
          path: "/", // La cookie estará disponible en toda la aplicación
          secure: true, // Solo permite que la cookie se envíe a través de HTTPS
          sameSite: "Strict", // Evita el envío de cookies a otros dominios (CSRF)
          expires: 1, // Opcional: la cookie expira después de 1 día
        });

        Cookies.set("u_i_d", response.data.usuarioId, {
          path: "/",
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        Cookies.set("r_u", response.data.rol, {
          path: "/",
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        useAuthStore.getState().setAuthData({
          rol: response.data.rol,
          jwttoken: response.data.jwttoken,
          token: response.data.token,
        });
        toast.success("¡Registro exitoso!");
        setOpen(true);
      }
    } catch (error: any) {
      if (error.response) {
        const errorData = error.response.data;
        toast.error(
          errorData?.subErrors[0]?.mensaje || "Hubo un error en el registro"
        );
      } else if (error.request) {
        toast.error("Error de conexión. Por favor, intente nuevamente");
      } else {
        toast.error("Error al procesar la solicitud");
      }
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Validación de todos los campos requeridos
  const isFormValid =
    isNombreValid &&
    isTelefonoValid &&
    isDireccionValid &&
    isDescripcionValid &&
    pais !== "" &&
    localidad.nombreLocalidad !== "" &&
    deportesSel.length > 0 &&
    moneda !== "" &&
    (!institucion || (institucion && nombreInstitucion.trim() !== "")) &&
    isSexoValid &&
    isFechaNacimientoValid &&
    isImagenValid;

  // Preparar opciones para los selectores
  const paisesOptions = paises.map(p => ({
    value: p.nombrePais,
    label: p.nombrePais
  }));

  const localidadesOptions = localidades.map(l => ({
    value: l.nombreLocalidad,
    label: l.nombreLocalidad
  }));

  const monedasOptions = monedas.map(m => ({
    value: m.nombreMoneda,
    label: m.nombreMoneda
  }));

  const deportesOptions = deportes.map(d => ({
    id: d.deporteId,
    value: d.nombre,
    label: d.nombre
  }));

  const sexoOptions = [
    { value: "masculino", label: "Masculino" },
    { value: "femenino", label: "Femenino" },
    { value: "otro", label: "Otro" }
  ];

  const handleDeporteAdd = (value: string) => {
    if (!deportesSel.includes(value)) {
      setDeportes([...deportesSel, value]);
    }
  };

  const handleDeporteRemove = (value: string) => {
    setDeportes(deportesSel.filter(deporte => deporte !== value));
  };

  const handlePaisChange = (value: string) => {
    setPais(value);
    const selectedPais = paises.find(p => p.nombrePais === value);
    if (selectedPais) {
      onPaisChange(selectedPais.id);
    }
  };

  const handleLocalidadChange = (value: string) => {
    const selectedLocalidad = localidades.find(l => l.nombreLocalidad === value);
    setLocalidad(selectedLocalidad || {
      localidadId: 0,
      nombreLocalidad: value,
      paisId: 0,
      pais: ""
    });
  };

  return (
    <div className="w-full relative pb-20 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 sm:p-10 rounded-xl mb-12 shadow-md">
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-xl md:text-2xl xl:text-3xl font-bold text-gray-800 mb-8">Perfil de Instructor</h2>

          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-4">
            <ImageUploader
              imagenPerfil={imagenPerfil}
              handleImageUpload={handleImageUpload}
              isImagenValid={isImagenValid}
              formSubmitted={formSubmitted}
              type="instructor"
              required={true}
            />

            <div className="flex flex-col items-center md:items-start">
              <p className="text-sm text-gray-500 mb-2">Correo electrónico:</p>
              <EmailDisplay
                email={email}
                style="instructor"
                className="mb-4"
              />
            </div>
          </div>
        </div>
      </div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 px-2 sm:px-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Columna izquierda - Información personal */}
        <div className="space-y-5 xl:space-y-8">
          <div className="border-b border-gray-200 pb-3 mb-6">
            <h3 className="text-base font-medium text-gray-700">Información Personal</h3>
          </div>

          {/* Nombre y Apellido */}
          <FormField
            id="nombre"
            label="Nombre y Apellido"
            value={nombre}
            onChange={handleNombreChange}
            placeholder="Nombre y Apellido completo"
            required
            isValid={isNombreValid}
            formSubmitted={formSubmitted}
            errorMessage="El nombre no puede estar vacío"
            className="mb-6"
          />

          {/* Fecha de Nacimiento */}
          <FormField
            id="fechaNacimiento"
            label="Fecha de Nacimiento"
            value={fechaNacimiento}
            onChange={handleFechaNacimientoChange}
            type="date"
            required
            isValid={isFechaNacimientoValid}
            formSubmitted={formSubmitted}
            errorMessage="Fecha requerida"
            className="mb-6"
          />

          {/* Sexo */}
          <SelectField
            id="sexo"
            label="Sexo"
            value={sexo}
            onValueChange={handleSexoChange}
            options={sexoOptions}
            placeholder="Selecciona tu sexo"
            required
            isValid={isSexoValid}
            formSubmitted={formSubmitted}
            errorMessage="Debes seleccionar un sexo"
            className="mb-6"
          />

          {/* Teléfono */}
          <FormField
            id="telefono"
            label="Teléfono"
            value={telefono}
            onChange={handleTelefonoChange}
            placeholder="Número de teléfono"
            type="tel"
            required
            isValid={isTelefonoValid}
            formSubmitted={formSubmitted}
            errorMessage="Número de teléfono inválido"
            className="mb-6"
          />

          <div className="border-b border-gray-200 pb-3 mb-6 mt-10">
            <h3 className="text-base font-medium text-gray-700">Ubicación</h3>
          </div>

          {/* País */}
          <SelectField
            id="pais"
            label="País"
            value={pais}
            onValueChange={handlePaisChange}
            options={paisesOptions}
            placeholder="Selecciona un país"
            required
            isValid={pais !== ""}
            formSubmitted={formSubmitted}
            errorMessage="Selecciona un país"
            className="mb-6"
          />

          {/* Localidad */}
          <SelectField
            id="localidad"
            label="Localidad"
            value={localidad.nombreLocalidad}
            onValueChange={handleLocalidadChange}
            options={localidadesOptions}
            placeholder="Selecciona una localidad"
            required
            isValid={localidad.nombreLocalidad !== ""}
            formSubmitted={formSubmitted}
            errorMessage="Selecciona una localidad"
            className="mb-6"
          />

          {/* Dirección */}
          <FormField
            id="direccion"
            label="Dirección"
            value={direccion}
            onChange={handleDireccionChange}
            placeholder="Dirección"
            required
            isValid={isDireccionValid}
            formSubmitted={formSubmitted}
            errorMessage="La dirección es requerida"
            className="mb-6"
          />
        </div>

        {/* Columna derecha - Información profesional */}
        <div className="space-y-8">
          <div className="border-b border-gray-200 pb-3 mb-6">
            <h3 className="text-base font-medium text-gray-700">Información Profesional</h3>
          </div>

          {/* Descripción */}
          <FormField
            id="descripcion"
            label="Descripción"
            value={descripcion}
            onChangeTextArea={handleDescripcionChange}
            placeholder="Describe tu experiencia como instructor, especialidades y estilo de enseñanza"
            required
            isValid={isDescripcionValid}
            formSubmitted={formSubmitted}
            errorMessage="La descripción no puede estar vacía"
            isTextArea={true}
            rows={5}
            className="mb-6"
          />

          {/* Deportes */}
          <MultiSelectField
            id="deportes"
            label="Deportes que enseñas"
            selectedValues={deportesSel}
            onValueChange={handleDeporteAdd}
            onRemove={handleDeporteRemove}
            options={deportesOptions}
            placeholder="Selecciona deportes"
            required
            isValid={deportesSel.length > 0}
            formSubmitted={formSubmitted}
            errorMessage="Selecciona al menos un deporte"
            className="mb-6"
          />

          {/* Moneda */}
          <SelectField
            id="moneda"
            label="Moneda para tus clases"
            value={moneda}
            onValueChange={setMoneda}
            options={monedasOptions}
            placeholder="Selecciona una moneda"
            required
            isValid={moneda !== ""}
            formSubmitted={formSubmitted}
            errorMessage="Selecciona una moneda"
            className="mb-6"
          />

          <div className="border-b border-gray-200 pb-3 mb-6 mt-10">
            <h3 className="text-base font-medium text-gray-700">Información Institucional</h3>
          </div>

          {/* Institución */}
          <InstitutionToggle
            institucion={institucion}
            setInstitucion={setInstitucion}
            nombreInstitucion={nombreInstitucion}
            setNombreInstitucion={setNombreInstitucion}
            numeroInstitucion={numeroInstitucion}
            setNumeroInstitucion={setNumeroInstitucion}
          />
        </div>
      </motion.div>

      {/* Botones de acción fijos */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-10">
        <div className="max-w-5xl mx-auto">
          <ActionButtons
            onBack={() => setStep(step - 1)}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isFormValid={isFormValid}
            buttonStyle="instructor"
            submitLabel="Completar Registro"
            backLabel="Paso Anterior"
          />
        </div>
      </div>
    </div>
  );
};

export default InstructorSignUp;
